import React, { useEffect, useState, useMemo } from "react";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Scales, FileText, ChartBar, Coins, CheckCircle, Warning, Info,
  Bank, Receipt, CreditCard, Buildings, Users, ListChecks, DownloadSimple,
  CaretDown, CaretRight, ArrowsClockwise, ChartPie, Sparkle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import AiExtractDialog from "@/components/app/AiExtractDialog";
import AuditAdjustmentsSection from "@/pages/finance/AuditAdjustmentsSection";

const PERIODS = [
  { v: "ytd",       l: "Year to Date" },
  { v: "year",      l: "Full Year" },
  { v: "quarter",   l: "Current Quarter" },
  { v: "month",     l: "Current Month" },
  { v: "last_year", l: "Last Year" },
  { v: "custom",    l: "Custom Range" },
];

// ──────────────────────────────────────────────────────────────
// Reusable atoms
// ──────────────────────────────────────────────────────────────
function Money({ value, tone = "text-slate-900", className = "" }) {
  const v = Number(value || 0);
  return (
    <span className={`font-mono-alt tabular-nums ${tone} ${className}`}>
      {formatCurrency(v)}
    </span>
  );
}

function Section({ icon: Icon, title, subtitle, right, children, testid, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="dc-card overflow-hidden" data-testid={testid}>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen((o) => !o); } }}
        className="flex w-full items-center gap-3 border-b border-slate-100 px-5 py-3 text-left hover:bg-slate-50/60 cursor-pointer select-none"
        data-testid={testid ? `${testid}-toggle` : undefined}
      >
        {open ? <CaretDown size={14} className="text-slate-500" /> : <CaretRight size={14} className="text-slate-500" />}
        {Icon && <Icon size={18} weight="fill" style={{ color: "#1E5CBA" }} />}
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-[15px] font-semibold text-slate-900">{title}</h3>
          {subtitle && <div className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">{subtitle}</div>}
        </div>
        {right && <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>{right}</div>}
      </div>
      {open && <div className="p-5">{children}</div>}
    </div>
  );
}

function LineRow({ label, value, indent = 0, bold = false, tone, divider = false, italic = false, testid }) {
  const padding = `pl-${indent * 4}`;
  return (
    <div
      className={`flex items-baseline justify-between gap-3 ${divider ? "border-t border-slate-200 pt-2 mt-2" : "py-1"} ${italic ? "italic" : ""}`}
      data-testid={testid}
    >
      <span
        className={`${bold ? "font-semibold text-slate-900" : "text-slate-600"} text-sm ${padding}`}
        style={indent ? { paddingLeft: `${indent * 16}px` } : {}}
      >
        {label}
      </span>
      <Money value={value} tone={tone || (bold ? "text-slate-900" : "text-slate-700")} className={bold ? "text-[15px]" : "text-sm"} />
    </div>
  );
}

function ValidationCard({ validations }) {
  if (!validations) return null;
  const errors = validations.filter((v) => v.severity === "error");
  const warnings = validations.filter((v) => v.severity === "warning");
  const infos = validations.filter((v) => v.severity === "info");

  if (validations.length === 0) {
    return (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 px-4 py-3 flex items-center gap-2" data-testid="audit-validations-pass">
        <CheckCircle size={18} weight="fill" className="text-emerald-600" />
        <div className="text-sm text-emerald-800 font-medium">All validation checks passed</div>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="audit-validations">
      {errors.map((v, i) => (
        <div key={`err-${i}`} className="rounded-lg border border-red-200 bg-red-50/60 px-4 py-2.5 flex items-start gap-2" data-testid={`validation-error-${v.rule}`}>
          <Warning size={16} weight="fill" className="text-red-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-red-800">
            <span className="font-semibold uppercase text-[10px] tracking-widest">Error · {v.rule}</span>
            <div className="text-red-900">{v.message}</div>
          </div>
        </div>
      ))}
      {warnings.map((v, i) => (
        <div key={`warn-${i}`} className="rounded-lg border border-amber-200 bg-amber-50/60 px-4 py-2.5 flex items-start gap-2" data-testid={`validation-warning-${v.rule}`}>
          <Warning size={16} weight="fill" className="text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-amber-800">
            <span className="font-semibold uppercase text-[10px] tracking-widest">Warning · {v.rule}</span>
            <div className="text-amber-900">{v.message}</div>
          </div>
        </div>
      ))}
      {infos.map((v, i) => (
        <div key={`info-${i}`} className="rounded-lg border border-blue-200 bg-blue-50/60 px-4 py-2.5 flex items-start gap-2" data-testid={`validation-info-${v.rule}`}>
          <Info size={16} weight="fill" className="text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <span className="font-semibold uppercase text-[10px] tracking-widest">Info · {v.rule}</span>
            <div className="text-blue-900">{v.message}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Statement: Profit & Loss
// ──────────────────────────────────────────────────────────────
function ProfitLossStatement({ income, period }) {
  return (
    <Section
      icon={ChartBar}
      title="1 · Statement of Profit or Loss"
      subtitle={`For the period · ${period.label}`}
      testid="audit-statement-pl"
    >
      <div className="space-y-1 max-w-2xl">
        <LineRow label="Revenue" value={income.revenue} bold tone="text-emerald-700" testid="pl-revenue" />
        <LineRow label="Direct / Project Costs" value={-income.direct_costs} tone="text-red-700" indent={1} testid="pl-direct-costs" />
        <LineRow label="Gross Profit" value={income.gross_profit} bold divider tone={income.gross_profit >= 0 ? "text-emerald-700" : "text-red-700"} testid="pl-gross-profit" />
        <LineRow label="Operating Expenses" value={-income.operating_expenses} tone="text-red-700" testid="pl-opex" />
        <LineRow label="— Admin Expenses" value={-income.admin_expenses} indent={2} italic testid="pl-admin-exp" />
        <LineRow label="— Overhead" value={-income.overhead} indent={2} italic testid="pl-overhead" />
        <LineRow label="Other Income" value={income.other_income} tone="text-emerald-700" testid="pl-other-income" />
        <LineRow label="Other Expenses" value={-income.other_expenses} tone="text-red-700" testid="pl-other-exp" />
        <LineRow label="Finance Costs" value={-income.finance_costs} tone="text-red-700" testid="pl-finance-costs" />
        <LineRow label="Profit Before Tax" value={income.profit_before_tax} bold divider tone={income.profit_before_tax >= 0 ? "text-emerald-700" : "text-red-700"} testid="pl-pbt" />
        <LineRow label="Tax Expense (≈15%)" value={-income.tax_expense} tone="text-red-700" indent={1} testid="pl-tax" />
        <LineRow label="Net Profit After Tax" value={income.net_profit} bold divider tone={income.net_profit >= 0 ? "text-emerald-700" : "text-red-700"} testid="pl-net-profit" />
      </div>
      <div className="mt-4 text-[11px] text-slate-500">
        Source: <span className="font-mono-alt">finance_entries</span> · <span className="font-mono-alt">hr_salary_batches</span> (70% direct / 30% admin allocation).
      </div>
    </Section>
  );
}

// ──────────────────────────────────────────────────────────────
// Statement: Balance Sheet
// ──────────────────────────────────────────────────────────────
function BalanceSheet({ bs }) {
  const balances = bs.balances;
  return (
    <Section
      icon={Scales}
      title="2 · Statement of Financial Position"
      subtitle={`As at ${formatDate(bs.as_of)}`}
      right={
        balances ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-emerald-700 border border-emerald-200" data-testid="bs-balance-ok">
            <CheckCircle size={11} weight="fill" /> Balances
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-red-700 border border-red-200" data-testid="bs-balance-warn">
            <Warning size={11} weight="fill" /> Does not balance
          </span>
        )
      }
      testid="audit-statement-bs"
    >
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        {/* Assets column */}
        <div>
          <div className="overline mb-2 text-emerald-700">Assets</div>
          <div className="space-y-1">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Current Assets</div>
            <LineRow label="Cash & bank balances" value={bs.current_assets.cash_and_bank} testid="bs-cash" />
            <LineRow label="Receivables" value={bs.current_assets.receivables} testid="bs-receivables" />
            <LineRow label="Total Current Assets" value={bs.current_assets.total} bold divider testid="bs-current-assets-total" />

            <div className="mt-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Non-Current Assets</div>
            <LineRow label="Fixed assets at cost" value={bs.non_current_assets.fixed_assets_cost} testid="bs-assets-cost" />
            <LineRow label="Less: accumulated depreciation" value={bs.non_current_assets.accumulated_depreciation} tone="text-red-700" indent={1} testid="bs-depreciation" />
            <LineRow label="Net Book Value" value={bs.non_current_assets.net_book_value} bold testid="bs-nbv" />

            <LineRow label="TOTAL ASSETS" value={bs.total_assets} bold divider tone="text-emerald-700" testid="bs-total-assets" />
          </div>
        </div>

        {/* Liabilities + Equity column */}
        <div>
          <div className="overline mb-2 text-red-700">Liabilities & Equity</div>
          <div className="space-y-1">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Current Liabilities</div>
            <LineRow label="Payables" value={bs.current_liabilities.payables} tone="text-red-700" testid="bs-payables" />
            <LineRow label="Tax liability" value={bs.current_liabilities.tax_liability} tone="text-red-700" testid="bs-tax-liab" />
            <LineRow label="Total Liabilities" value={bs.total_liabilities} bold tone="text-red-700" divider testid="bs-total-liabilities" />

            <div className="mt-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Equity</div>
            <LineRow label="Retained Earnings" value={bs.equity.retained_earnings} testid="bs-retained" />
            <LineRow label="Total Equity" value={bs.equity.total} bold testid="bs-equity-total" />

            <LineRow label="TOTAL LIABILITIES + EQUITY" value={bs.total_liabilities_and_equity} bold divider tone="text-blue-700" testid="bs-total-le" />
          </div>
        </div>
      </div>
      {!balances && (
        <div className="mt-4 rounded-lg border border-red-200 bg-red-50/60 px-4 py-2 text-xs text-red-800">
          <Warning size={12} weight="fill" className="inline mr-1" />
          The accounting equation Assets = Liabilities + Equity does not balance. Review source records before relying on this report.
        </div>
      )}
    </Section>
  );
}

// ──────────────────────────────────────────────────────────────
// Statement: Changes in Equity
// ──────────────────────────────────────────────────────────────
function EquityStatement({ equity, period }) {
  return (
    <Section icon={Coins} title="3 · Statement of Changes in Equity" subtitle={`For the period · ${period.label}`} testid="audit-statement-equity">
      <div className="space-y-1 max-w-xl">
        <LineRow label="Opening Balance" value={equity.opening_balance} bold testid="eq-opening" />
        <LineRow label="Owner Contributions" value={equity.owner_contributions} indent={1} testid="eq-contrib" />
        <LineRow label="Profit / (Loss) for the Period" value={equity.profit_for_period} indent={1} tone={equity.profit_for_period >= 0 ? "text-emerald-700" : "text-red-700"} testid="eq-profit" />
        <LineRow label="Drawings / Distributions" value={-equity.drawings} indent={1} tone="text-red-700" testid="eq-drawings" />
        <LineRow label="Other Movements" value={equity.other_movements} indent={1} testid="eq-other" />
        <LineRow label="Closing Balance" value={equity.closing_balance} bold divider tone="text-blue-700" testid="eq-closing" />
      </div>
    </Section>
  );
}

// ──────────────────────────────────────────────────────────────
// Statement: Cash Flow
// ──────────────────────────────────────────────────────────────
function CashFlowStatement({ cf, period }) {
  return (
    <Section
      icon={Bank}
      title="4 · Statement of Cash Flows"
      subtitle={`Indirect method · ${period.label}`}
      right={
        cf.reconciles ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-emerald-700 border border-emerald-200" data-testid="cf-reconcile-ok">
            <CheckCircle size={11} weight="fill" /> Reconciled
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-amber-700 border border-amber-200" data-testid="cf-reconcile-warn">
            <Warning size={11} weight="fill" /> Drift vs BS cash
          </span>
        )
      }
      testid="audit-statement-cf"
    >
      <div className="space-y-1 max-w-2xl">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mt-1">Operating Activities</div>
        <LineRow label="Net Profit" value={cf.operating_activities.net_profit} indent={1} testid="cf-net-profit" />
        <LineRow label="Add: Depreciation" value={cf.operating_activities.depreciation} indent={1} testid="cf-depreciation" />
        <LineRow label="Change in Receivables" value={cf.operating_activities.change_receivables} indent={1} testid="cf-change-ar" />
        <LineRow label="Change in Payables" value={cf.operating_activities.change_payables} indent={1} testid="cf-change-ap" />
        {cf.operating_activities.other_working_capital !== undefined && (
          <LineRow label="Other working-capital movements" value={cf.operating_activities.other_working_capital} indent={1} testid="cf-other-wc" />
        )}
        <LineRow label="Cash from Operating Activities" value={cf.operating_activities.total} bold tone={cf.operating_activities.total >= 0 ? "text-emerald-700" : "text-red-700"} testid="cf-operating-total" />

        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mt-3">Investing Activities</div>
        <LineRow label="Asset Purchases" value={cf.investing_activities.asset_purchases} indent={1} tone="text-red-700" testid="cf-asset-purchases" />
        <LineRow label="Cash from Investing Activities" value={cf.investing_activities.total} bold tone={cf.investing_activities.total >= 0 ? "text-emerald-700" : "text-red-700"} testid="cf-investing-total" />

        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mt-3">Financing Activities</div>
        <LineRow label="Cash from Financing Activities" value={cf.financing_activities.total} bold testid="cf-financing-total" />

        <LineRow label="Net Change in Cash" value={cf.net_change_in_cash} bold divider tone={cf.net_change_in_cash >= 0 ? "text-emerald-700" : "text-red-700"} testid="cf-net-change" />
        <LineRow label="Opening Cash" value={cf.opening_cash} testid="cf-opening" />
        <LineRow label="Closing Cash (computed)" value={cf.computed_closing} bold testid="cf-computed-closing" />
        <LineRow label="Closing Cash (per Balance Sheet)" value={cf.closing_cash} bold tone="text-blue-700" testid="cf-bs-closing" />
      </div>
    </Section>
  );
}

// ──────────────────────────────────────────────────────────────
// Notes
// ──────────────────────────────────────────────────────────────
function NotesSection({ notes }) {
  const breakdowns = [
    { title: "Revenue Breakdown", data: notes.revenue_breakdown, tone: "emerald" },
    { title: "Project Cost Breakdown", data: notes.project_costs, tone: "red" },
    { title: "Overhead / Operating Expense Breakdown", data: notes.overhead_breakdown, tone: "amber" },
  ];

  return (
    <Section icon={FileText} title="5 · Notes to the Financial Statements" subtitle="Basis of preparation · Accounting policies · Disclosures" testid="audit-statement-notes" defaultOpen={false}>
      <div className="space-y-4">
        <div>
          <div className="overline mb-1">Basis of preparation</div>
          <p className="text-sm text-slate-700 leading-relaxed">{notes.basis_of_preparation}</p>
        </div>
        <div>
          <div className="overline mb-1">Accounting policies</div>
          <ul className="space-y-1.5 text-sm text-slate-700 list-disc pl-5">
            {(notes.accounting_policies || []).map((p, i) => <li key={i}>{p}</li>)}
          </ul>
        </div>
        {breakdowns.map((b) => {
          const entries = Object.entries(b.data || {});
          if (entries.length === 0) return null;
          return (
            <div key={b.title}>
              <div className="overline mb-2">{b.title}</div>
              <div className="space-y-1 max-w-xl" data-testid={`notes-${b.title.toLowerCase().split(' ')[0]}`}>
                {entries.map(([cat, amt]) => (
                  <LineRow key={cat} label={cat || "—"} value={amt} indent={1} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
}

// ──────────────────────────────────────────────────────────────
// Supporting schedules
// ──────────────────────────────────────────────────────────────
function ScheduleTable({ rows, columns, emptyText = "No records", testid }) {
  if (!rows || rows.length === 0) {
    return <div className="text-xs text-slate-400 py-2" data-testid={testid ? `${testid}-empty` : undefined}>{emptyText}</div>;
  }
  return (
    <div className="responsive-table-wrap">
      <table className="data-table w-full text-xs" data-testid={testid}>
        <thead>
          <tr>{columns.map((c) => <th key={c.k} className={c.align === "right" ? "text-right" : ""}>{c.l}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || r.invoice_no || r.asset_no || r.ref || r.batch_no || r.project_id || i}>
              {columns.map((c) => (
                <td key={c.k} className={`px-3 py-1.5 ${c.align === "right" ? "text-right font-mono-alt tabular-nums" : ""}`}>
                  {c.fmt ? c.fmt(r[c.k], r) : (r[c.k] ?? "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SupportingSchedules({ schedules }) {
  const bb = schedules.bank_balances;
  const ar = schedules.receivables_aging;
  const ap = schedules.payables_aging;
  const fa = schedules.fixed_asset_schedule;
  const pr = schedules.payroll_summary;
  const pcs = schedules.project_cost_summary;
  const bva = schedules.budget_vs_actual;
  const kr = schedules.key_ratios;
  const yoy = schedules.year_over_year;

  return (
    <Section icon={ListChecks} title="6 · Supporting Schedules" subtitle="Schedules that support the figures shown above" testid="audit-schedules" defaultOpen={false}>
      <div className="space-y-6">
        {/* Key ratios */}
        <div>
          <div className="overline mb-2 flex items-center gap-1.5"><ChartPie size={11} weight="fill" /> Key Financial Ratios</div>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
            <RatioTile label="Gross Margin" value={`${kr.gross_margin_pct}%`} tone={kr.gross_margin_pct >= 20 ? "emerald" : kr.gross_margin_pct >= 0 ? "amber" : "red"} testid="ratio-gross-margin" />
            <RatioTile label="Net Margin" value={`${kr.net_margin_pct}%`} tone={kr.net_margin_pct >= 10 ? "emerald" : kr.net_margin_pct >= 0 ? "amber" : "red"} testid="ratio-net-margin" />
            <RatioTile label="Current Ratio" value={kr.current_ratio === null ? "—" : kr.current_ratio} tone={kr.current_ratio === null ? "slate" : kr.current_ratio >= 1.5 ? "emerald" : kr.current_ratio >= 1 ? "amber" : "red"} testid="ratio-current" />
            <RatioTile label="Equity Ratio" value={`${kr.equity_ratio_pct}%`} tone={kr.equity_ratio_pct >= 40 ? "emerald" : kr.equity_ratio_pct >= 20 ? "amber" : "red"} testid="ratio-equity" />
          </div>
        </div>

        {/* Year over year */}
        <div>
          <div className="overline mb-2">Year-over-Year Comparison</div>
          <ScheduleTable
            testid="schedule-yoy"
            rows={yoy.rows || []}
            columns={[
              { k: "year", l: "Year" },
              { k: "revenue", l: "Revenue", align: "right", fmt: (v) => formatCurrency(v) },
              { k: "direct_costs", l: "Direct Costs", align: "right", fmt: (v) => formatCurrency(v) },
              { k: "net_profit", l: "Net Profit", align: "right", fmt: (v) => formatCurrency(v) },
            ]}
          />
        </div>

        {/* Bank balances */}
        <div>
          <div className="overline mb-2 flex items-center gap-1.5"><Bank size={11} weight="fill" /> Bank Balances · as of {formatDate(bb.as_of)}</div>
          <ScheduleTable
            testid="schedule-bank"
            rows={bb.accounts}
            columns={[
              { k: "account", l: "Account" },
              { k: "balance_usd", l: "Balance USD", align: "right", fmt: (v) => formatCurrency(v) },
            ]}
          />
          <div className="mt-2 flex justify-end text-sm font-semibold">Total: <span className="ml-2 font-mono-alt tabular-nums text-blue-700">{formatCurrency(bb.total)}</span></div>
        </div>

        {/* AR aging */}
        <div>
          <div className="overline mb-2 flex items-center gap-1.5"><Receipt size={11} weight="fill" /> Receivables Aging · Total {formatCurrency(ar.total_outstanding)}</div>
          <div className="grid grid-cols-4 gap-2 mb-2 text-xs" data-testid="schedule-ar-buckets">
            {Object.entries(ar.buckets || {}).map(([k, v]) => (
              <div key={k} className="rounded-md border border-slate-200 px-2 py-1.5 bg-slate-50/60">
                <div className="overline">{k.replace("_", "–")} days</div>
                <div className="font-mono-alt tabular-nums font-semibold">{formatCurrency(v)}</div>
              </div>
            ))}
          </div>
          <ScheduleTable
            testid="schedule-ar"
            rows={(ar.rows || []).slice(0, 15)}
            emptyText="No outstanding receivables"
            columns={[
              { k: "invoice_no", l: "Invoice" },
              { k: "client", l: "Client" },
              { k: "due_date", l: "Due", fmt: (v) => formatDate(v) || "—" },
              { k: "amount", l: "Amount", align: "right", fmt: (v) => formatCurrency(v) },
              { k: "outstanding", l: "Outstanding", align: "right", fmt: (v) => formatCurrency(v) },
              { k: "bucket", l: "Bucket" },
            ]}
          />
        </div>

        {/* AP aging */}
        <div>
          <div className="overline mb-2 flex items-center gap-1.5"><CreditCard size={11} weight="fill" /> Payables Aging · Total {formatCurrency(ap.total_outstanding)}</div>
          <div className="grid grid-cols-4 gap-2 mb-2 text-xs" data-testid="schedule-ap-buckets">
            {Object.entries(ap.buckets || {}).map(([k, v]) => (
              <div key={k} className="rounded-md border border-slate-200 px-2 py-1.5 bg-slate-50/60">
                <div className="overline">{k.replace("_", "–")} days</div>
                <div className="font-mono-alt tabular-nums font-semibold">{formatCurrency(v)}</div>
              </div>
            ))}
          </div>
          <ScheduleTable
            testid="schedule-ap"
            rows={(ap.rows || []).slice(0, 15)}
            emptyText="No outstanding payables"
            columns={[
              { k: "ref", l: "Ref" },
              { k: "title", l: "Title" },
              { k: "vendor", l: "Vendor" },
              { k: "amount_estimated", l: "Estimated", align: "right", fmt: (v) => formatCurrency(v) },
              { k: "amount_outstanding", l: "Outstanding", align: "right", fmt: (v) => formatCurrency(v) },
              { k: "bucket", l: "Bucket" },
            ]}
          />
        </div>

        {/* Fixed assets */}
        <div>
          <div className="overline mb-2 flex items-center gap-1.5"><Buildings size={11} weight="fill" /> Fixed Asset Register & Depreciation · NBV {formatCurrency(fa.total_nbv)}</div>
          <div className="grid grid-cols-3 gap-2 mb-2 text-xs" data-testid="schedule-fa-totals">
            <div className="rounded-md border border-slate-200 px-2 py-1.5 bg-slate-50/60">
              <div className="overline">Total Cost</div>
              <div className="font-mono-alt tabular-nums font-semibold">{formatCurrency(fa.total_cost)}</div>
            </div>
            <div className="rounded-md border border-slate-200 px-2 py-1.5 bg-slate-50/60">
              <div className="overline">Accumulated Depreciation</div>
              <div className="font-mono-alt tabular-nums font-semibold text-red-700">{formatCurrency(fa.total_depreciation)}</div>
            </div>
            <div className="rounded-md border border-slate-200 px-2 py-1.5 bg-slate-50/60">
              <div className="overline">Net Book Value</div>
              <div className="font-mono-alt tabular-nums font-semibold text-emerald-700">{formatCurrency(fa.total_nbv)}</div>
            </div>
          </div>
          <ScheduleTable
            testid="schedule-fa"
            rows={(fa.rows || []).slice(0, 15)}
            emptyText="No fixed assets recorded"
            columns={[
              { k: "asset_no", l: "Asset No." },
              { k: "name", l: "Name" },
              { k: "category", l: "Category" },
              { k: "purchase_date", l: "Acquired", fmt: (v) => formatDate(v) || "—" },
              { k: "useful_life_years", l: "Life (yrs)" },
              { k: "cost", l: "Cost", align: "right", fmt: (v) => formatCurrency(v) },
              { k: "accumulated_depreciation", l: "Acc. Dep.", align: "right", fmt: (v) => formatCurrency(v) },
              { k: "net_book_value", l: "NBV", align: "right", fmt: (v) => formatCurrency(v) },
            ]}
          />
        </div>

        {/* Payroll */}
        <div>
          <div className="overline mb-2 flex items-center gap-1.5"><Users size={11} weight="fill" /> Payroll Summary · Total {formatCurrency(pr.total)}</div>
          <ScheduleTable
            testid="schedule-payroll"
            rows={pr.rows || []}
            emptyText="No payroll batches in this period"
            columns={[
              { k: "batch_no", l: "Batch" },
              { k: "month", l: "Month" },
              { k: "staff_count", l: "Staff", align: "right" },
              { k: "total_amount_usd", l: "Amount USD", align: "right", fmt: (v) => formatCurrency(v) },
              { k: "status", l: "Status" },
            ]}
          />
        </div>

        {/* Project Cost Summary */}
        <div>
          <div className="overline mb-2">Project Cost Summary · Revenue {formatCurrency(pcs.total_revenue)} · Cost {formatCurrency(pcs.total_cost)}</div>
          <ScheduleTable
            testid="schedule-projects"
            rows={(pcs.rows || []).slice(0, 25)}
            emptyText="No project finance activity in this period"
            columns={[
              { k: "project_name", l: "Project" },
              { k: "revenue", l: "Revenue", align: "right", fmt: (v) => formatCurrency(v) },
              { k: "cost", l: "Cost", align: "right", fmt: (v) => formatCurrency(v) },
              { k: "margin", l: "Margin", align: "right", fmt: (v) => formatCurrency(v) },
              { k: "margin_pct", l: "Margin %", align: "right", fmt: (v) => `${v}%` },
            ]}
          />
        </div>

        {/* Budget vs Actual */}
        <div>
          <div className="overline mb-2">Budget vs Actual · Budget {formatCurrency(bva.total_budget)} · Actual {formatCurrency(bva.total_actual)}</div>
          <ScheduleTable
            testid="schedule-budget"
            rows={(bva.rows || []).slice(0, 25)}
            emptyText="No projects with budgets configured"
            columns={[
              { k: "project_name", l: "Project" },
              { k: "status", l: "Status" },
              { k: "budget", l: "Budget", align: "right", fmt: (v) => formatCurrency(v) },
              { k: "actual", l: "Actual", align: "right", fmt: (v) => formatCurrency(v) },
              { k: "variance", l: "Variance", align: "right", fmt: (v, r) => <span className={r.overrun ? "text-red-700" : "text-emerald-700"}>{formatCurrency(v)}</span> },
              { k: "usage_pct", l: "Used %", align: "right", fmt: (v, r) => <span className={r.overrun ? "text-red-700" : ""}>{v}%</span> },
            ]}
          />
        </div>
      </div>
    </Section>
  );
}

function RatioTile({ label, value, tone, testid }) {
  const TONE = {
    emerald: "border-emerald-200 bg-emerald-50/60 text-emerald-800",
    amber: "border-amber-200 bg-amber-50/60 text-amber-800",
    red: "border-red-200 bg-red-50/60 text-red-800",
    slate: "border-slate-200 bg-slate-50/60 text-slate-700",
  }[tone] || "border-slate-200 bg-slate-50/60 text-slate-700";
  return (
    <div className={`rounded-lg border px-3 py-2 ${TONE}`} data-testid={testid}>
      <div className="overline">{label}</div>
      <div className="font-display text-xl font-bold tabular-nums">{value}</div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Main Audit Report Tab
// ──────────────────────────────────────────────────────────────
export default function AuditReportTab({ canEdit }) {
  const [period, setPeriod] = useState("ytd");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [baselines, setBaselines] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { period };
      if (period === "custom") {
        if (customFrom) params.custom_from = customFrom;
        if (customTo) params.custom_to = customTo;
      }
      const { data: report } = await api.get("/finance-audit/report", { params });
      setData(report);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const loadBaselines = async () => {
    try {
      const { data: bl } = await api.get("/finance/audit-baselines");
      setBaselines(bl.items || []);
    } catch { /* silent */ }
  };

  useEffect(() => { load(); loadBaselines(); /* eslint-disable-next-line */ }, []);
  // Auto-reload when period changes (except custom waits for explicit refresh)
  useEffect(() => {
    if (period !== "custom") load();
    // eslint-disable-next-line
  }, [period]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const params = { period };
      if (period === "custom") {
        if (customFrom) params.custom_from = customFrom;
        if (customTo) params.custom_to = customTo;
      }
      const res = await api.get("/finance-audit/report.pdf", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `descon_audit_report_${data?.period?.start || ""}_${data?.period?.end || ""}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      toast.success("Audit report PDF downloaded");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDownloading(false);
    }
  };

  const onBaselineSaved = async () => {
    toast.success("Audit baseline saved · refreshing report");
    await loadBaselines();
    await load();
  };

  if (loading && !data) {
    return <div className="font-mono-alt text-xs uppercase tracking-[0.25em] text-zinc-500">Loading audit report…</div>;
  }

  if (!data) {
    return <div className="text-sm text-slate-500">No audit data available.</div>;
  }

  const errCount = (data.validations || []).filter((v) => v.severity === "error").length;
  const warnCount = (data.validations || []).filter((v) => v.severity === "warning").length;

  return (
    <div className="space-y-5" data-testid="audit-report-tab">
      {/* Header / controls strip */}
      <div className="dc-card p-5">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <div className="overline mb-1">Internal Finance Audit Report</div>
            <div className="font-display text-xl font-bold text-slate-900" data-testid="audit-period-label">{data.period.label}</div>
            <div className="font-mono-alt text-[11px] text-slate-500">
              {data.period.start} → {data.period.end} · Generated {data.generated_at ? formatDate(data.generated_at) : "now"}
            </div>
          </div>
          <div className="ml-auto flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Reporting period</Label>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="h-9 w-44" data-testid="audit-period-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PERIODS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {period === "custom" && (
              <>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">From</Label>
                  <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="h-9 w-40" data-testid="audit-custom-from" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">To</Label>
                  <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="h-9 w-40" data-testid="audit-custom-to" />
                </div>
              </>
            )}
            <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="audit-refresh-btn">
              <ArrowsClockwise size={12} className={`mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button size="sm" className="btn-primary-blue" onClick={downloadPdf} disabled={downloading} data-testid="audit-download-pdf">
              <DownloadSimple size={12} className={`mr-1 ${downloading ? "animate-pulse" : ""}`} /> {downloading ? "Generating…" : "Download PDF"}
            </Button>
          </div>
        </div>
        {/* Validation summary chips */}
        <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
          <Chip tone={data.statements.balance_sheet.balances ? "emerald" : "red"} icon={data.statements.balance_sheet.balances ? CheckCircle : Warning} testid="chip-bs">
            Balance Sheet · {data.statements.balance_sheet.balances ? "Balances" : "Out of balance"}
          </Chip>
          <Chip tone={data.statements.cash_flow.reconciles ? "emerald" : "amber"} icon={data.statements.cash_flow.reconciles ? CheckCircle : Warning} testid="chip-cf">
            Cash Flow · {data.statements.cash_flow.reconciles ? "Reconciles" : "Drift"}
          </Chip>
          <Chip tone={errCount === 0 ? (warnCount === 0 ? "emerald" : "amber") : "red"} icon={errCount === 0 && warnCount === 0 ? CheckCircle : Warning} testid="chip-validations">
            {errCount} error{errCount === 1 ? "" : "s"} · {warnCount} warning{warnCount === 1 ? "" : "s"}
          </Chip>
          <span className="ml-auto text-[11px] font-mono-alt text-slate-500">
            EAT timezone · electronically generated by Descon Management System · no manual signature required
          </span>
        </div>
      </div>

      {/* Validation card */}
      <ValidationCard validations={data.validations} />

      {/* The 5 statements */}
      <ProfitLossStatement income={data.statements.income_statement} period={data.period} />
      <BalanceSheet bs={data.statements.balance_sheet} />
      <EquityStatement equity={data.statements.equity_statement} period={data.period} />
      <CashFlowStatement cf={data.statements.cash_flow} period={data.period} />
      <NotesSection notes={data.statements.notes} />

      {/* Supporting schedules */}
      <SupportingSchedules schedules={data.schedules} />

      {/* Audit baseline upload (preserved feature) */}
      <Section
        icon={Sparkle}
        title="Audit Baseline · External Auditor Sign-off"
        subtitle={baselines.length > 0
          ? `Baselines on file · ${baselines.map((b) => b.fiscal_year).join(" · ")}`
          : "Upload an external auditor report to reconcile against live figures"}
        right={
          canEdit ? (
            <AiExtractDialog
              module="audit_report"
              onSaved={onBaselineSaved}
              saveLabel="Save audit baseline"
              trigger={
                <Button size="sm" className="btn-primary-blue" data-testid="audit-baseline-upload" onClick={(e) => e.stopPropagation()}>
                  <Sparkle size={12} className="mr-1" /> Upload external audit (AI Extract)
                </Button>
              }
            />
          ) : null
        }
        testid="audit-baseline-section"
        defaultOpen={false}
      >
        <div className="text-sm text-slate-600">
          External auditor reports can be ingested via AI Extract to capture the year-end signed figures. The system then reconciles these audited baselines against live data, surfacing any drift in a separate variance report.
        </div>
        {baselines.length === 0 && (
          <div className="mt-3 text-xs text-slate-500 italic">
            No external audit baselines uploaded yet. The figures above are computed from live system data.
          </div>
        )}
        {baselines.length > 0 && (
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
            {baselines.map((b) => (
              <div key={b.fiscal_year} className="rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2" data-testid={`baseline-tile-${b.fiscal_year}`}>
                <div className="overline">FY {b.fiscal_year}</div>
                <div className="font-mono-alt text-[11px] text-slate-600">Net: <span className="font-semibold">{formatCurrency(b.net)}</span></div>
                <div className="font-mono-alt text-[11px] text-slate-600">Rev: {formatCurrency(b.revenue)}</div>
              </div>
            ))}
          </div>
        )}
      </Section>

      <AuditAdjustmentsSection canEdit={canEdit} />
    </div>
  );
}

function Chip({ tone, icon: Icon, children, testid }) {
  const TONE = {
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    red: "bg-red-50 text-red-800 border-red-200",
    blue: "bg-blue-50 text-blue-800 border-blue-200",
  }[tone] || "bg-slate-50 text-slate-700 border-slate-200";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${TONE}`} data-testid={testid}>
      {Icon && <Icon size={11} weight="fill" />}
      {children}
    </span>
  );
}
